/**
 * 图片压缩工具
 * 支持将图片压缩到指定大小
 */

/**
 * 压缩图片到指定大小
 * @param {File} file - 原始图片文件
 * @param {number} maxSizeKB - 目标大小（KB），默认700KB
 * @param {number} quality - 初始压缩质量，默认0.8
 * @param {number} maxWidth - 最大宽度，默认1920
 * @param {number} maxHeight - 最大高度，默认1080
 * @returns {Promise<File>} 压缩后的文件
 */
export const compressImage = (file, maxSizeKB = 700, quality = 0.8, maxWidth = 1920, maxHeight = 1080) => {
  return new Promise((resolve, reject) => {
    // 检查是否为图片文件
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    // 如果文件已经小于目标大小，直接返回
    if (file.size <= maxSizeKB * 1024) {
      resolve(file);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        // 计算压缩后的尺寸
        let { width, height } = calculateDimensions(img.width, img.height, maxWidth, maxHeight);
        
        canvas.width = width;
        canvas.height = height;

        // 绘制图片到canvas
        ctx.drawImage(img, 0, 0, width, height);

        // 对于非JPG格式的图片，统一转换为JPG以获得更好的压缩效果
        let targetMimeType = file.type;
        let targetFileName = file.name;
        
        if (file.type !== 'image/jpeg' && file.type !== 'image/jpg') {
          targetMimeType = 'image/jpeg';
          // 替换文件扩展名
          targetFileName = file.name.replace(/\.\w+$/i, '.jpg');
          console.log(`转换图片格式以优化压缩: ${file.name} (${file.type}) -> ${targetFileName} (image/jpeg)`);
        }

        // 尝试不同的质量设置来达到目标大小
        compressToTargetSize(canvas, targetFileName, targetMimeType, maxSizeKB, quality)
          .then(resolve)
          .catch(reject);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('图片加载失败'));
    };

    // 读取图片
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => {
      reject(new Error('文件读取失败'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * 计算压缩后的尺寸，保持宽高比
 */
const calculateDimensions = (originalWidth, originalHeight, maxWidth, maxHeight) => {
  let width = originalWidth;
  let height = originalHeight;

  // 如果图片尺寸超过最大限制，按比例缩放
  if (width > maxWidth || height > maxHeight) {
    const widthRatio = maxWidth / width;
    const heightRatio = maxHeight / height;
    const ratio = Math.min(widthRatio, heightRatio);

    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  return { width, height };
};

/**
 * 压缩到目标文件大小
 */
const compressToTargetSize = (canvas, fileName, mimeType, maxSizeKB, initialQuality) => {
  return new Promise((resolve, reject) => {
    let quality = initialQuality;
    let attempts = 0;
    const maxAttempts = 10;
    
    const tryCompress = () => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('图片压缩失败'));
          return;
        }

        const sizeKB = blob.size / 1024;
        console.log(`压缩尝试 ${attempts + 1}/${maxAttempts}: ${sizeKB.toFixed(1)}KB (目标: ${maxSizeKB}KB, 质量: ${quality.toFixed(2)})`);
        
        // 如果大小合适或已达到最大尝试次数，返回结果
        if (sizeKB <= maxSizeKB || attempts >= maxAttempts || quality <= 0.1) {
          const compressedFile = new File([blob], fileName, {
            type: mimeType,
            lastModified: Date.now()
          });
          console.log(`压缩完成: ${fileName} -> ${sizeKB.toFixed(1)}KB`);
          resolve(compressedFile);
          return;
        }

        // 根据当前大小调整质量
        attempts++;
        const ratio = maxSizeKB / sizeKB;
        quality = Math.max(0.1, quality * ratio * 0.9); // 稍微保守一点

        // 递归尝试
        setTimeout(tryCompress, 10);
      }, mimeType, quality);
    };

    tryCompress();
  });
};

/**
 * 批量压缩图片
 * @param {File[]} files - 文件数组
 * @param {number} maxSizeKB - 目标大小（KB）
 * @returns {Promise<File[]>} 压缩后的文件数组
 */
export const compressImages = async (files, maxSizeKB = 700) => {
  const compressedFiles = [];
  
  for (const file of files) {
    try {
      const compressedFile = await compressImage(file, maxSizeKB);
      compressedFiles.push(compressedFile);
    } catch (error) {
      
      // 如果压缩失败，使用原文件
      compressedFiles.push(file);
    }
  }
  
  return compressedFiles;
};

/**
 * 格式化文件大小显示
 * @param {number} bytes - 字节数
 * @returns {string} 格式化后的大小字符串
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * 检查文件类型是否为图片
 * @param {File} file - 文件对象
 * @returns {boolean} 是否为图片
 */
export const isImageFile = (file) => {
  return file && file.type && file.type.startsWith('image/');
};
